import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import '../../../component-styles/PublicProposalPage.css';
import API_BASE from '../../../utils/api.js';
import InvoicePdfDocument from './InvoicePdfDocument';
import PublicStripePaymentModal from './PublicStripePaymentModal';
import notify from '../../../utils/notify';
import { depositStillDue } from '../../../utils/invoiceAdjustments.js';

const ERROR_MESSAGES = {
  expired:
    'This invoice link has expired. Please reach out to your designer to request a new one.',
  not_found:
    'This invoice link is no longer active. Please contact your designer for an updated link.',
  network:
    "We couldn't load this invoice right now. Please try refreshing the page, or contact your designer if the problem continues.",
};

export default function PublicInvoicePage() {
  const { shareToken: routeShareToken } = useParams();
  const location = useLocation();
  const pdfRef = useRef(null);

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const shareToken = routeShareToken || new URLSearchParams(location.search).get('publicInvoice');

  /* -----------------------------------------------------
     LOAD INVOICE
  ----------------------------------------------------- */
  useEffect(() => {
    if (!shareToken) return;

    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/invoices/public/${encodeURIComponent(shareToken)}`
        );
        if (res.status === 410) throw new Error('expired');
        if (!res.ok) throw new Error('not_found');
        setInvoice(await res.json());
      } catch (err) {
        setError(err.message || 'network');
      } finally {
        setLoading(false);
      }
    })();
  }, [shareToken, API_BASE]);

  /* -----------------------------------------------------
     DYNAMIC PAGE TITLE
  ----------------------------------------------------- */
  useEffect(() => {
    if (!invoice) return;
    const num = invoice.invoiceNumber ? `Invoice #${invoice.invoiceNumber}` : 'Invoice';
    const firm = invoice.companyName ? ` — ${invoice.companyName}` : '';
    document.title = `${num}${firm}`;
    return () => {
      document.title = 'InteriorFolio';
    };
  }, [invoice]);

  /* -----------------------------------------------------
     SAFE DEFAULTS
  ----------------------------------------------------- */
  function formatStatus(status) {
    if (!status) return '';
    return status
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  /* -----------------------------------------------------
     EARLY RETURNS
  ----------------------------------------------------- */
  if (loading) return <div className="public-loading">Loading invoice…</div>;
  if (error)
    return (
      <div className="public-error">
        {ERROR_MESSAGES[error] || ERROR_MESSAGES.network}
      </div>
    );
  if (!invoice) return null;

  const {
    billingAddress,
    shippingAddress,
    invoiceNumber,
    coverImageUrl,
    company,
    invoiceTemplate,
  } = invoice;

  const companyName = company?.companyName || invoice.companyName;
  const logoUrl = company?.logoUrl || invoice.logoUrl;

  const total = invoice.totals?.total ?? 0;
  const paid = invoice.totals?.paid ?? 0;
  const remainingBalance = Math.max(Number((total - paid).toFixed(2)), 0);
  const isFullyPaid = remainingBalance <= 0;
  const depositDue = isFullyPaid ? 0 : depositStillDue(invoice.totals?.depositRequested, paid);

  /* -----------------------------------------------------
     STATUS UPDATE (PUBLIC)
  ----------------------------------------------------- */
  async function updateStatus(newStatus) {
    try {
      const res = await fetch(`${API_BASE}/api/invoices/public/${shareToken}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!res.ok) throw new Error();
      notify(`Invoice marked as ${formatStatus(newStatus)}`);
      setInvoice((prev) => ({ ...prev, status: newStatus }));
    } catch {
      notify('Failed to update invoice');
    }
  }

  /* -----------------------------------------------------
     PDF DOWNLOAD
  ----------------------------------------------------- */
  function downloadPDF() {
    if (!pdfRef.current) return;
    html2pdf()
      .set({
        margin: [10, 10, 10, 10],
        filename: `Invoice-${invoiceNumber || invoice.invoiceId}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#fff' },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] },
      })
      .from(pdfRef.current)
      .save();
  }

  /* -----------------------------------------------------
     RENDER
  ----------------------------------------------------- */
  return (
    <div className="public-proposal-wrapper">
      <header className="public-topbar">
        {logoUrl ? (
          <img src={`${API_BASE}/api/upload/proxy-image?url=${encodeURIComponent(logoUrl)}`} alt={companyName || 'Your designer'} />
        ) : companyName ? (
          <span className="public-firm-name">{companyName}</span>
        ) : null}
      </header>

      <div className="public-layout">
        <div className="public-content">
          <div id="proposal-content" className="public-proposal-card" ref={pdfRef}>
            <InvoicePdfDocument
              invoice={invoice}
              company={company || { companyName, address: billingAddress || shippingAddress }}
              apiBase={API_BASE}
              template={invoiceTemplate}
            />
          </div>
        </div>

        {coverImageUrl && (
          <aside className="public-cover" style={{ backgroundImage: `url(${API_BASE}/api/upload/proxy-image?url=${encodeURIComponent(coverImageUrl)})` }} />
        )}
      </div>

      {Array.isArray(invoice.attachments) && invoice.attachments.length > 0 && (
        <div className="public-content" style={{ marginTop: -8 }}>
          <div className="public-proposal-card" style={{ padding: '16px 24px' }}>
            <h3 style={{ margin: '0 0 10px', fontSize: 15 }}>Attachments</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {invoice.attachments.map((a) => (
                <a
                  key={a.id || a.url}
                  href={a.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: 14 }}
                >
                  {a.name || 'Download attachment'}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="public-actions">
        <button className="deny-btn" onClick={() => updateStatus('changes_requested')}>
          Request Changes
        </button>
        {!isFullyPaid && (
          <button className="approve-btn" onClick={() => setShowPayment(true)}>
            Pay Now
          </button>
        )}
        <button className="pdf-btn" onClick={downloadPDF}>
          Download PDF
        </button>
      </div>

      {showPayment && (
        <PublicStripePaymentModal
          shareToken={shareToken}
          createIntentEndpoint={`${API_BASE}/api/invoices/public/${shareToken}/create-payment-intent`}
          remainingBalance={remainingBalance}
          defaultAmount={depositDue}
          invoiceNumber={invoiceNumber}
          onSuccess={(amount, data) => {
            setInvoice((prev) => ({
              ...prev,
              totals: {
                ...prev.totals,
                paid: Number(((prev.totals?.paid ?? 0) + amount).toFixed(2)),
              },
              status: data?.newStatus || prev.status,
            }));
            setShowPayment(false);
            notify('Payment submitted successfully');
          }}
          onClose={() => setShowPayment(false)}
        />
      )}
    </div>
  );
}
